import moment from "moment";
import {
  Alert,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Switch,
} from "antd";
import type { CheckboxValueType } from "antd/es/checkbox/Group";
import { RangePickerProps } from "antd/lib/date-picker";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/rootReducer";
import { AccountType, Actions } from "../../shared/constant";
import { Max100Validate } from "../../utils/validate";
import dateUtils from "../../utils/dateUtils";

function IncomeShareForm({
  mode,
  incomeShare,
  incomeShareForm,
  clientForm,
}: {
  mode: string;
  incomeShare: any;
  incomeShareForm: any;
  clientForm: any;
}) {
  const [checkedValues, setCheckedValues] = useState<CheckboxValueType[]>([]);
  const [isEDRequired, setIsEDRequired] = useState(mode !== Actions.EDIT);
  const { clientTypes, existedClientShare } = useSelector(
    (state: RootState) => state.account,
  );

  const getCheckboxValues = () => {
    return [
      incomeShare.temp_label_charge && "label",
      incomeShare.temp_artist_charge && "artist",
      incomeShare.temp_composer_charge && "composer",
      incomeShare.temp_lyricist_charge && "lyricist",
    ];
  };

  useEffect(() => {
    if (mode === Actions.EDIT && incomeShare) {
      setCheckedValues(getCheckboxValues());
      incomeShareForm.setFieldsValue({
        checkbox_group: getCheckboxValues(),
        label_charge: incomeShare.temp_label_charge,
        artist_charge: incomeShare.temp_artist_charge,
        lyricist_charge: incomeShare.temp_lyricist_charge,
        composer_charge: incomeShare.temp_composer_charge,
        // effective_date: moment(incomeShare.effective_date),
        clap_service_charge: incomeShare.temp_clap_service_charge,
        is_service_charge_visible: incomeShare.is_service_charge_visible,
        /* is_service_charge_deduction_payment:
          incomeShare.is_service_charge_deduction_payment,
        is_service_charge_deduction_invoice:
          incomeShare.is_service_charge_deduction_invoice, */
        invoice_name: incomeShare.invoice_name,
        income_share_phone: incomeShare.phone,
        address: incomeShare.address,
      });
    }
  }, [incomeShare]);

  // useEffect(() => {
  //   console.log("Existed client share", existedClientShare);
  // }, [existedClientShare]);

  const isEffectiveDateRequired = () => {
    if (mode === Actions.EDIT && incomeShare) {
      const fields = incomeShareForm.getFieldsValue();
      // console.log("fields right now: ", fields, "api value", incomeShare);
      const c1 = Number(fields.label_charge || 0);
      const c2 = Number(fields.artist_charge || 0);
      const c3 = Number(fields.lyricist_charge || 0);
      const c4 = Number(fields.composer_charge || 0);
      const c5 = Number(fields.clap_service_charge || 0);
      const fieldsArr = [c1, c2, c3, c4, c5];

      const i1 = Number(incomeShare.temp_label_charge || 0);
      const i2 = Number(incomeShare.temp_artist_charge || 0);
      const i3 = Number(incomeShare.temp_lyricist_charge || 0);
      const i4 = Number(incomeShare.temp_composer_charge || 0);
      const i5 = Number(incomeShare.temp_clap_service_charge || 0);
      const incomeShareArr = [i1, i2, i3, i4, i5];

      // console.log(fieldsArr, incomeShareArr);

      const isEqual =
        fieldsArr.length === incomeShareArr.length &&
        fieldsArr.every((val, index) => val === incomeShareArr[index]);

      if (isEqual) setIsEDRequired(false);
      else setIsEDRequired(true);
    } else {
      setIsEDRequired(true);
    }
  };

  const onChange = (values: CheckboxValueType[]) => {
    setCheckedValues(values);
    isEffectiveDateRequired();
  };

  const handleChange = () => {
    isEffectiveDateRequired();
  };

  const isDisable = (item: string) => {
    if (checkedValues.includes(item)) return false;
    return true;
  };

  const validateIncomeShare = () => {
    const fields = incomeShareForm.getFieldsValue();
    const c1 = Number(fields.label_charge || 0);
    const c2 = Number(fields.artist_charge || 0);
    const c3 = Number(fields.composer_charge || 0);
    const c4 = Number(fields.lyricist_charge || 0);
    const sumOfShare = c1 + c2 + c3 + c4;

    if (sumOfShare > 100) return Promise.reject(new Error("Max 100% in total"));
    return Promise.resolve();
  };

  const onChekboxChange = (e: any) => {
    // FIXME don't clear the values; on edit it clears
    switch (e) {
      case "label":
        incomeShareForm.setFieldsValue({
          label_charge: undefined,
        });
        break;
      case "artist":
        incomeShareForm.setFieldsValue({
          artist_charge: undefined,
        });
        break;
      case "composer":
        incomeShareForm.setFieldsValue({
          composer_charge: undefined,
        });
        break;
      case "lyricist":
        incomeShareForm.setFieldsValue({
          lyricist_charge: undefined,
        });
        break;

      default:
        break;
    }
  };

  const getAlertJSX = () => {
    return (
      <div>
        <div className="pb-1 text-danger text-[15px]">
          The client you are trying to add is already present as an artist on
          the following clients:
        </div>
        {existedClientShare?.map((item: any) => (
          <div>
            Client name: {item.client_name} | Share: {item.share_percentage}%
          </div>
        ))}
      </div>
    );
  };

  const isRequiredOnClientType = (t: string) => {
    const fields = clientForm.getFieldsValue();
    // const cTypes = clientTypes
    //   .filter((item: any) => fields.client_types.includes(item.id))
    //   .map((item: any) => item.label);

    // if (cTypes.includes(t)) return true;
    // return false;

    const type = clientTypes.find(
      (item: any) => fields.client_types === item.id,
    );
    if (type.label === t) return true;
    return false;
  };

  const disabledDate: RangePickerProps["disabledDate"] = (current) => {
    // Can not select days before today
    return current && current < moment().startOf("day");
  };

  return (
    <div className="mb-7">
      {mode === Actions.EDIT && (
        <div>
          <div className="mb-1.5 text-[15px]">
            Current percentage of Income Share
          </div>
          <div className="grid grid-cols-2 gap-x-6 mb-4">
            <div className="default-border p-2">
              {incomeShare?.label_charge && (
                <div className="flex justify-between">
                  <div>Producer</div>
                  <div>{incomeShare.label_charge}</div>
                </div>
              )}
              {incomeShare?.artist_charge && (
                <div className="flex justify-between">
                  <div>Artist</div>
                  <div>{incomeShare.artist_charge}</div>
                </div>
              )}
              {incomeShare?.composer_charge && (
                <div className="flex justify-between">
                  <div>Composer</div>
                  <div>{incomeShare.composer_charge}</div>
                </div>
              )}
              {incomeShare?.lyricist_charge && (
                <div className="flex justify-between">
                  <div>Lyricist</div>
                  <div>{incomeShare.lyricist_charge}</div>
                </div>
              )}
            </div>
            <div>
              <div className="flex justify-between">
                <div>Current CLAP Service charge (%)</div>
                <div>{incomeShare?.clap_service_charge}</div>
              </div>
              <div className="flex justify-between pt-1">
                <div>Current Effective Date</div>
                <div>
                  {dateUtils.defaultFormat(incomeShare?.effective_date)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-x-6 mb-4">
        <div>
          <Form.Item
            name="checkbox_group"
            label="Percentage of Income Share (Max 100% in total)"
            rules={[{ required: true, message: "Income share is required" }]}
          >
            <Checkbox.Group onChange={onChange} className="customCheckbox">
              <div className="grid grid-cols-2">
                <Checkbox
                  value="label"
                  className="leading-8"
                  onChange={() => onChekboxChange("label")}
                >
                  Producer
                </Checkbox>
                <Form.Item
                  name="label_charge"
                  rules={[
                    {
                      required: isRequiredOnClientType(AccountType.LABEL),
                      message: "Producer share required",
                    },
                    { validator: () => validateIncomeShare() },
                  ]}
                >
                  <InputNumber
                    className="w-full"
                    placeholder="Income Share %"
                    disabled={isDisable("label")}
                    onChange={handleChange}
                  />
                </Form.Item>
                <Checkbox
                  value="artist"
                  className="leading-8"
                  onChange={() => onChekboxChange("artist")}
                >
                  Artist
                </Checkbox>
                <Form.Item
                  name="artist_charge"
                  rules={[
                    {
                      required: isRequiredOnClientType(AccountType.ARTIST),
                      message: "Artist share required",
                    },
                    { validator: () => validateIncomeShare() },
                  ]}
                >
                  <InputNumber
                    className="w-full"
                    placeholder="Income Share %"
                    disabled={isDisable("artist")}
                    onChange={handleChange}
                  />
                </Form.Item>
                <Checkbox
                  value="composer"
                  className="leading-8"
                  onChange={() => onChekboxChange("composer")}
                >
                  Composer
                </Checkbox>
                <Form.Item
                  name="composer_charge"
                  rules={[
                    {
                      required: isRequiredOnClientType(AccountType.COMPOSER),
                      message: "Composer share required",
                    },
                    { validator: () => validateIncomeShare() },
                  ]}
                >
                  <InputNumber
                    className="w-full"
                    placeholder="Income Share %"
                    disabled={isDisable("composer")}
                    onChange={handleChange}
                  />
                </Form.Item>
                <Checkbox
                  value="lyricist"
                  className="leading-8"
                  onChange={() => onChekboxChange("lyricist")}
                >
                  Lyricist
                </Checkbox>
                <Form.Item
                  name="lyricist_charge"
                  rules={[
                    {
                      required: isRequiredOnClientType(AccountType.LYRICIST),
                      message: "Lyricist share required",
                    },
                    { validator: () => validateIncomeShare() },
                  ]}
                >
                  <InputNumber
                    className="w-full"
                    placeholder="Income Share %"
                    disabled={isDisable("lyricist")}
                    onChange={handleChange}
                  />
                </Form.Item>
                {/* <Checkbox value="rightHolder" className="leading-8">
                  Rigth Holder
                </Checkbox>
                <Form.Item name="right_holder_charge">
                  <InputNumber
                    className="w-full"
                    placeholder="Income Share %"
                    disabled={isDisable("rightHolder")}
                  />
                </Form.Item>
                <Checkbox value="onbehalf" className="leading-8">
                  On-behalf
                </Checkbox>
                <Form.Item name="on_behalf_charge">
                  <InputNumber
                    className="w-full"
                    placeholder="Income Share %"
                    disabled={isDisable("onbehalf")}
                  />
                </Form.Item> */}
              </div>
            </Checkbox.Group>
          </Form.Item>
        </div>
        <div>
          <Form.Item
            name="effective_date"
            label={
              mode === Actions.EDIT ? "New Effective Date" : "Effective Date"
            }
            rules={[{ required: isEDRequired }]}
          >
            <DatePicker className="w-full" disabledDate={disabledDate} />
          </Form.Item>
          <Form.Item
            name="clap_service_charge"
            label="CLAP Service charge (%)"
            rules={[
              { required: true },
              { validator: (_: any, value: number) => Max100Validate(value) },
            ]}
          >
            <InputNumber className="w-full" onChange={handleChange} />
          </Form.Item>
          <div className="flex gap-3 items-center">
            <Form.Item
              valuePropName="checked"
              name="is_service_charge_visible"
              className="h-4"
              initialValue={false}
            >
              <Switch />
            </Form.Item>
            <div className="h-5">Show CLAP service charge</div>
          </div>
          <div className="text-xs text-secondary tracking-wide mt-1">
            If enabled CLAP service charge will show in the client details page
            {/* eslint-disable-next-line react/no-unescaped-entities */} as well
            as in user's (Accounts) reports section.
          </div>
          {/*           <div className="grid grid-flow-col items-center">
            <Form.Item
              valuePropName="checked"
              name="is_service_charge_deduction_payment"
              className="h-4"
              initialValue
            >
              <Switch defaultChecked />
            </Form.Item>
            <div className="h-5">Show payments deducting service charge</div>
          </div>
          <div className="text-xs text-secondary tracking-wide mt-1 mb-2">
            If enabled all payment value in the application will show after
            deducting the service charge.
          </div>
          <div className="grid grid-flow-col items-center">
            <Form.Item
              valuePropName="checked"
              name="is_service_charge_deduction_invoice"
              className="h-4"
              initialValue
            >
              <Switch />
            </Form.Item>
            <div className="h-5">Show service charge deduction in invoice</div>
          </div>
          <div className="text-xs text-secondary tracking-wide mt-1">
            If enabled a message will show in the invoice indicating service
            charge being deducted from income.
          </div> */}
        </div>
      </div>
      {!!existedClientShare?.length && (
        <Alert message={getAlertJSX()} type="warning" className="mb-4" />
      )}
      <div className="btn-border" />
      <div className="grid grid-cols-2 gap-x-6 mt-3">
        <Form.Item
          name="invoice_name"
          label="Invoice Name"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="income_share_phone" label="Phone">
          <Input />
        </Form.Item>
      </div>
      <Form.Item name="address" label="Address">
        <Input.TextArea />
      </Form.Item>
    </div>
  );
}

export default IncomeShareForm;
